import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import { DrawingOptions, DrawingSettingGroup, DrawingTypes, SettingValues } from '../drawing.interface'
import { MouseEventParams, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { getTimeIndex } from '../../../../util/chart'
import { scaledCoordinate } from '../drawing.helper'
import { ChartEvents } from '../../chart.events'
import { ContractSwitch } from '../../chart.typings'

export type ContractSwitchType = 'contractSwitch'

export type ContractSwitchState = {
    data: ContractSwitch[]
}

export type ContractSwitchOptions = {
    drawingType: ContractSwitchType
}

export type ContractSwitchDrawingSettings = {
    data: ContractSwitch[]
}

class ContractSwitchTool extends DrawingBase<ContractSwitchOptions, ContractSwitchDrawingSettings, ContractSwitchState> {
    private findHoveredData(x: number, y: number): ContractSwitch | null {
        const sx = scaledCoordinate(x, this.horizontalPixelRatio)
        const sy = scaledCoordinate(y, this.verticalPixelRatio)
        const data = this._state.data
        if (!data) {
            return null
        }
        const circleRadius = 10 * window.devicePixelRatio
        const marginBottom = 4 * window.devicePixelRatio
        const canvasHeight = this.canvasHeight - circleRadius - marginBottom
        const primaryData = this._chart.getRenderedPrimaryData()

        if (!primaryData) {
            return null
        }

        return data.find((d) => {
            const index = getTimeIndex(primaryData, d.time / 1000)
            if (!Number.isFinite(index)) {
                return false
            }

            const xCoordinate = scaledCoordinate(
                this._chartApi.timeScale().timeToCoordinate(primaryData[index].time) as number,
                this.horizontalPixelRatio
            )

            if (!xCoordinate) {
                return false
            }
            const yCoordinate = canvasHeight
            const dx = sx - xCoordinate
            const dy = sy - yCoordinate
            const distance = Math.sqrt(dx * dx + dy * dy)
            return distance <= circleRadius
        }) || null
    }

    private selectedTime: number | null = null

    private setCrosshairAtPoint(point: { x: number; y: number }, time: number) {
        this._chart.setCrossHairPosition(
            this._chart.primarySeriesGroup?.getPrimarySeriesApi()?.coordinateToPrice(point.y) ?? 0,
            time / 1000
        )
        this._chartApi.applyOptions({
            crosshair: {
                vertLine: {
                    color: 'rgb(124, 77, 255)',
                    visible: true
                },
                horzLine: {
                    visible: false
                }
            }
        })
    }

    constructor(options: DrawingOptions<any, any, any>) {
        super(options)
        if (!this._chartApi) return
        this._chartApi.subscribeClick(this.handleCircleClick.bind(this))
        this._chartApi.timeScale().subscribeVisibleTimeRangeChange(() => {
            this._chart.publishEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, null)
        })
    }

    private handleCircleClick(param: MouseEventParams): void {
        const { point } = param
        if (!point) return
        const hoveredData = this.findHoveredData(point.x, point.y)
        if (hoveredData) {
            if (this.selectedTime === hoveredData.time) {
                this.selectedTime = null
                this._chart.publishEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, null)
            } else {
                this.selectedTime = hoveredData.time
                this._chart.publishEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, {
                    x: point.x,
                    y: point.y - 10,
                    type: this.getDrawingType(),
                    time: hoveredData.time,
                    fromContract: hoveredData.fromContract,
                    toContract: hoveredData.toContract
                })
                this.setCrosshairAtPoint(point, hoveredData.time)
            }
        } else {
            this.selectedTime = null
            this._chart.publishEvent(ChartEvents.CONTRACT_SWITCH_CLICKED, null)
        }
        this.primitive?.requestUpdate()
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        const data = this._state.data
        if (!data || data.length === 0) return

        const visibleLogicalRange = this._chartApi.timeScale().getVisibleLogicalRange()
        if (!visibleLogicalRange) return
        const barsInfo = this.getSeries().barsInLogicalRange(visibleLogicalRange)
        if (!barsInfo) return
        ctx.globalCompositeOperation = 'source-over'
        const primaryData = this._chart.getRenderedPrimaryData()
        if (!primaryData) return

        const dpr = window.devicePixelRatio || 1

        const iconPath = new Path2D('M10 12.22L17.78 20H32.32M32.22 20L26.11 13.89M32.22 20L26.11 26.11M10 27.78L14.44 23.32')
        const iconBoxSize = 44 // Based on your path's coordinate range (~10-32)
        const iconScaleFactor = 0.9 // % of circle diameter to occupy

        data.forEach((contractSwitch) => {
            if (!contractSwitch) return

            const index = getTimeIndex(primaryData, contractSwitch.time / 1000)
            const timeCoord = this._chartApi.timeScale().timeToCoordinate(primaryData[index]?.time)
            if (timeCoord == null) return

            const xCoordinate = scaledCoordinate(timeCoord, this.horizontalPixelRatio)
            if (!xCoordinate || !ctx) return

            ctx.save()

            const circleRadius = 10 * dpr
            const marginBottom = 4 * dpr
            const canvasHeight = this.canvasHeight - circleRadius * 2 - marginBottom

            ctx.translate(xCoordinate - circleRadius, canvasHeight)

            // Draw outer filled circle
            ctx.beginPath()
            ctx.arc(circleRadius, circleRadius, circleRadius, 0, Math.PI * 2)
            ctx.fillStyle = this.selectedTime === contractSwitch.time ? 'rgb(124, 77, 255)' : this._chart.theme.palette.background.paper
            ctx.fill()
            ctx.closePath()

            // Draw inner circle stroke
            ctx.beginPath()
            ctx.arc(circleRadius, circleRadius, circleRadius - 2 * dpr, 0, Math.PI * 2)
            ctx.strokeStyle = this.selectedTime === contractSwitch.time ? 'white' : 'rgb(124, 77, 255)'
            ctx.lineWidth = 1.5 * dpr
            ctx.stroke()
            ctx.closePath()

            // Draw icon, scaled and centered inside the circle
            ctx.save()
            ctx.translate(circleRadius, circleRadius) // Move origin to circle center

            const desiredSize = circleRadius * 2 * iconScaleFactor
            const iconScale = desiredSize / iconBoxSize

            ctx.scale(iconScale, iconScale)
            const iconOffsetX = 21.16 // (10 + 32.32) / 2
            const iconOffsetY = 20    // (12.22 + 27.78) / 2
            ctx.translate(-iconOffsetX, -iconOffsetY)

            ctx.strokeStyle = this.selectedTime === contractSwitch.time ? 'white' : 'rgb(124, 77, 255)'
            ctx.lineWidth = 1.5 * dpr
            ctx.stroke(iconPath)
            ctx.restore()

            ctx.restore()
        })
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const hoveredData = this.findHoveredData(x, y)
        if (hoveredData) {
            this._hovered = true
            this._chart.setCrossHairPosition(
                this._chart.primarySeriesGroup?.getPrimarySeriesApi()?.coordinateToPrice(y) ?? 0,
                hoveredData.time / 1000
            )
            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: 'rgb(124, 77, 255)',
                        visible: true
                    },
                    horzLine: {
                        visible: false
                    }
                }
            })
            return {
                externalId: this.id,
                zOrder: 'top',
                cursorStyle: 'pointer'

            }
        } else if (this._hovered) {
            this._hovered = false
            this._chartApi.applyOptions({
                crosshair: {
                    vertLine: {
                        color: '#758696',
                        visible: true
                    },
                    horzLine: {
                        visible: true
                    }
                }

            })
        }

        return null
    }

    updateHandle = (id: string, time: Time, price: number) => {

    }

    onSettingsUpdate = (settings: Partial<ContractSwitchDrawingSettings>) => {
        if (settings.data) {
            this._state.data = settings.data
        }
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.primitive?.requestUpdate()

    }

    getAvailableSettings = (): DrawingSettingGroup<ContractSwitchDrawingSettings, SettingValues>[] => {
        return []
    }

    getDrawingType(): DrawingTypes {
        switch (this.options.drawingType) {
            case 'contractSwitch':
                return 'contractSwitch'
        }
    }

    getSettingsValues(): ContractSwitchDrawingSettings {
        return { ...this._settings }
    }

    getMiniSettings = () => {
        return []
    }
}

export default ContractSwitchTool